import { createClient, SupabaseClient } from "@supabase/supabase-js";

const migrationModules = import.meta.glob<string>('../sql/migrations/*.sql', { 
  eager: true,
  query: '?raw',
  import: 'default'
});

const enumModules = import.meta.glob<string>('../sql/app_role_enum.sql', { 
  eager: true,
  query: '?raw',
  import: 'default'
});

export class DatabaseMigrator {
  /**
   * Loads and sorts the migrations from a folder
   * 
   * IMPORTANT: The SQL files must be named with a numeric prefix to define 
   * the execution order based on dependencies. Example:
   * - 01_organizations.sql (no dependencies)
   * - 02_profiles.sql
   * - 03_memberships.sql (depends on organizations)
   * - etc.
   * 
   * This way, the alphabetical order will automatically respect the dependencies
   * without needing to maintain a list of order in the code.
   */
  private loadMigrations(modules: Record<string, string>): Array<{ name: string; sql: string }> {
    return Object.entries(modules)
      .map(([path, sql]) => {
        const name = path.split('/').pop()?.replace('.sql', '') || 'unknown';
        return { name, sql };
      })
      .sort((a, b) => a.name.localeCompare(b.name)); 
  }
  private async executeSqlWithRetries(
    adminClient: SupabaseClient,
    sql: string, 
    description: string, 
    retries = 3, 
    delay = 2000
  ): Promise<boolean> {
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        try {
          const { error } = await adminClient.rpc('exec_sql', { sql });
          
          if (!error) {
            console.log(`${description} ejecutado exitosamente`);
            return true;
          }
          if (error.code === "PGRST202") {
            throw new Error("exec_sql function not available");
          }

        } catch (rpcError) {
          console.warn(`La función exec_sql no está disponible o hubo un error:`, rpcError);
          
          const statements = sql
            .split(';')
            .map(stmt => stmt.trim())
            .filter(stmt => stmt.length > 0);

          for (const stmt of statements) {
            if (stmt.toLowerCase().includes('create table')) {
              const tableName = stmt.match(/create\s+table\s+(?:if\s+not\s+exists\s+)?([^\s(]+)/i)?.[1]?.trim();
              if (tableName) {
                try {
                  const cleanTableName = tableName.replace(/^public\./, '').replace(/"/g, '');
                  await adminClient.from(cleanTableName).select('count(*)').limit(1);
                  continue;
                } catch (tableError) {
                  // Table doesn't exist, continue with creation
                }
              }
            }
            try {
              const { error } = await adminClient.rpc('exec_sql', { sql: stmt + ';' });
              if (!error) {
                continue;
              } else if (error.code !== "PGRST202") {
                console.warn(`Error al ejecutar instrucción individual:`, error);
              }
            } catch (stmtError) {
              console.warn(`Error al ejecutar instrucción individual:`, stmtError);
            }
          }
        }
        
        return true; 
      } catch (error) {
        console.error(`Error al ejecutar ${description} (intento ${attempt}/${retries}):`, error);
        
        if (attempt < retries) {
          await new Promise(resolve => setTimeout(resolve, delay));
          delay = Math.min(delay * 1.5, 10000); 
        }
      }
    }
    
    console.error(`No se pudo ejecutar ${description} después de ${retries} intentos`);
    return false;
  }

  async createOrganizationSchema(
    supabaseUrl: string,
    serviceKey: string
  ): Promise<boolean> {
    let adminClient: SupabaseClient | null = null;
    
    try {
      adminClient = createClient(supabaseUrl, serviceKey, {
        auth: {
          persistSession: false,
          autoRefreshToken: false,
        },
      });

      const enums = this.loadMigrations(enumModules);
      for (const enumMigration of enums) {
        console.log(`Executing enum: ${enumMigration.name}`);
        await this.executeSqlWithRetries(adminClient, enumMigration.sql, `Tipo enum ${enumMigration.name}`);
      }

      const migrations = this.loadMigrations(migrationModules);
      
      for (const migration of migrations) {
        console.log(`Executing migration: ${migration.name}`);
        const success = await this.executeSqlWithRetries(adminClient, migration.sql, `Migración ${migration.name}`);
        if (!success) {
          console.error(`Migration ${migration.name} failed - continuing with next migration`);
        }
      }

      return true;
    } catch (error) {
      console.error("Error creating organization schema:", error);
      return false;
    } finally {
      if (adminClient) {
        try {
          await adminClient.auth.signOut();
        } catch (cleanupError) {
          console.warn("Error cleaning up admin client:", cleanupError);
        }
      }
    }
  }

}